import { Col, Row, Form, Button, Spinner, Stack, ListGroup } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useEffect, useRef, useState } from 'react'
import { ClaimFileDto, ClaimFormDto, ClaimProfesionalDto, SendEmailClaimDto } from "../../types";
import { sendEmailClaim } from "../../api/ClaimFollowUpAPI";
import AlertMessage from "../../../../components/AlertMessage";
import { useAlert } from "../../../../hooks/useAlert";
import { useClaimFollowUpTypesSelect } from "../../../admin/Dictionaries/hooks/useClaimFollowUpTypesSelect";
import ClaimAttachDocumentsModal from "../modals/ClaimAttachDocumentsModal";
import SelectedFilterChip from "../../../../components/Filters/components/SelectedFilterChip";
import { getProfessionalsBySearchText } from "../../../profesionals/api/professionalAPI";
import { useServiceIndicatorSelect } from "../../../admin/Dictionaries/hooks/getServiceIndicatorSelectsByClient";
import { useClaimProfessionalTypeSelect } from "../../../admin/Dictionaries/hooks/getClaimProfessionalTypeSelectByClient";
import { ProfessionalFormDto } from "../../../profesionals/types";

interface EditClaimFollowUpSendEmailProps {
    claim: ClaimFormDto;
    setSendEmail: React.Dispatch<React.SetStateAction<boolean>>;
    comment: string;
    professionals: ClaimProfesionalDto[];
    claimUpdate: ClaimFormDto;
    setClaimUpdate: React.Dispatch<React.SetStateAction<ClaimFormDto | undefined>>;
    onSaved: () => void;
}

export default function EditClaimFollowUpSendEmail({ claim, setSendEmail, comment, professionals, claimUpdate, setClaimUpdate, onSaved }: EditClaimFollowUpSendEmailProps) {
    const { t } = useTranslation(['claim', 'common']);
    const [loading, setLoading] = useState(false);
    const { data: claimFollowUpTypes } = useClaimFollowUpTypesSelect({});
    const [showAttachDocumentsModal, setShowAttachDocumentsModal] = useState(false);    
    const { showAlert, hideAlert, alert, isVisible } = useAlert({ fromLocation: false });

    const [professionalsSearch, setProfessionalsSearch] = useState<ClaimProfesionalDto[]>([]);

    const [query, setQuery] = useState("");
    const [open, setOpen] = useState(false);
    const comboRef = useRef<HTMLDivElement>(null);

    const { data: serviceIndicators } = useServiceIndicatorSelect({});
    const { data: claimProfessionalTypes } = useClaimProfessionalTypeSelect({});

    const getServiceDescription = (id?: number | null): string =>
        id == null
            ? ""
            : serviceIndicators?.find((s) => s.id === id)?.name ?? "";

    const getClaimProfessionalTypesDescription = (id?: number | null): string =>
        id == null
            ? ""
            : claimProfessionalTypes?.find((s) => s.id === id)?.name ?? "";


    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (comboRef.current && !comboRef.current.contains(e.target as Node)) {
                setOpen(false);
            }
        };
        document.addEventListener("click", handleClickOutside);
        return () => document.removeEventListener("click", handleClickOutside);
    }, []);

    useEffect(() => {

        let cancelled = false;
        const fetchData = async () => {
            try {
                const result = await getProfessionalsBySearchText({ text: query });
                /*
                const filteredResult = result.filter(
                    p => !professionals.some(
                        professional => professional.professionalId === p.id
                    )
                );*/

                if (!cancelled) setProfessionalsSearch(result);
            } catch (err) {
                if (!cancelled) setProfessionalsSearch([]);
            }
        };

        fetchData();

        return () => {
            cancelled = true;
        };
    }, [query]);

    const subject: string = `Departament de qualitat i atenció a la ciutadania - Incidència # ${claim.id}`;
    const message: string = `
        Estimat/ada Dr/a <<Escriu el nom del doctor>>\n

        Al departament de qualitat i atenció a la ciutadania hem rebut la incidència ${claim.id} per part d'un usuari del nostre centre

        \nEl motiu de la consulta es: ${comment}\n

        Amb l'esperit de millorar el nostre sistema de qualitat i la nostre atenció a la ciutadania volem tenir en compte tots els professionals implicats i per això oferim la possibilitat de realitzar un escrit per tal d'ajudar-nos a gestionar aquesta incidència

        \nGràcies per la seva colaboració
    `;

    const [formData, setFormData] = useState<SendEmailClaimDto>({ claimId: claim.id, emails: "", subject: subject, message: message });

    const [errors, setErrors] = useState<{ emails?: string, subject?: string, message?: string }>({});



    const handleSendEmail = async () => {
        const newErrors: { emails?: string, subject?: string, message?: string } = {};
        if (!formData.emails.trim()) {
            newErrors.emails = t("common:form.requiredFieldForm");
        }
        if (!formData.subject.trim()) {
            newErrors.subject = t("common:form.requiredFieldForm");
        }
        if (!formData.message.trim()) {
            newErrors.message = t("common:form.requiredFieldForm");
        }
        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }

        try {
            setLoading(true);
            await sendEmailClaim(formData);
            setLoading(false);
            setSendEmail(false)
            showAlert({
                    message: t("claim:detail.messageSendMessageSuccessfully"),
                    type: "success",
                    duration: 5000,
                });
            onSaved();
        } catch (e: any) {
            setLoading(false); // Reactiva el botón
            let errorMessage =
                e?.response?.data?.message || e?.message || t("common:genericFormErrors.unexpectedErorr");
            showAlert({
                message: errorMessage,
                type: "danger",
                duration: 3000,
            });
        }
    }
    
    function handleProfessionalClick(professional: ProfessionalFormDto) {
        const emailsArray = formData.emails.split(';').filter(Boolean); // separa los emails existentes

        const updatedEmails = Array.from(new Set([...emailsArray, professional.email]));

        setFormData({
            ...formData,
            emails: updatedEmails.join(';') // mantiene la cadena limpia sin ; extra
        });
    }
    const fileInputRef = useRef<HTMLInputElement | null>(null);

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = Array.from(e.target.files || []);
        if (files.length === 0) return;

        setFormData((prev) => {
            const existing = Array.isArray(prev.files)
                ? prev.files
                : prev.files
                    ? [prev.files]
                    : [];
            return {
                ...prev,
                files: [...existing, ...files]
            };
        });

        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }
    };

    const handleRemoveFile = (indexToRemove: number) => {
        const nextFiles = (formData.files || []).filter((_, index) => index !== indexToRemove);
        setFormData((prev) => ({
            ...prev,
            files: nextFiles.length > 0 ? nextFiles : undefined
        }));
    };

    const handleRemoveClaimFile = (indexToRemove: number) => {
        const nextFiles = (formData.claimFiles || []).filter((_, index) => index !== indexToRemove);
        setFormData((prev) => ({
            ...prev,
            claimFiles: nextFiles.length > 0 ? nextFiles : undefined,
        }));
    };
    
    const addAttachedDocuments = (data: { selectedClaimFiles?: ClaimFileDto[]; addedFiles?: File[] }) => {
        setFormData((prev) => {
            return {
                ...prev,
                files: [...(data.addedFiles || [])],
                claimFiles: [...(data.selectedClaimFiles || [])]
            };
        });
    }

    return (
        <div className="active-section">
            <Row className="w-100">
                <ClaimAttachDocumentsModal
                    claimId={claim.id}
                    show={showAttachDocumentsModal}
                    possibleClaimFiles={claim.files}
                    onClose={() => setShowAttachDocumentsModal(false)}
                    onSubmit={addAttachedDocuments}
                />
                {loading && (
                    <div className="overlay-spinner">
                        <Spinner animation="border" variant="light" />
                    </div>
                )}
                {
                isVisible && (
                    <AlertMessage
                        message={ alert!.message }
                        type = { alert!.type }
                        onClose = { hideAlert }/>
                    )
                }
                <Row >
                    <p className="hint">{t("claim:newClaimInputProfessionalSubtitle")}</p>
                </Row >
                <Row>
                    <div className="combo" ref={comboRef}>
                        <div className="combo-input-row">
                            <Form.Control
                                type="text"
                                placeholder={t("claim:newClaimInputProfessionalSearchPlaceholder")}
                                autoComplete="off"
                                value={query}
                                onChange={(e) => {
                                    setQuery(e.target.value);
                                    setOpen(true);
                                }}
                                onFocus={() => setOpen(true)}
                            />
                        </div>
                    </div>
                    {open && (
                        <ListGroup className="dropdown">
                            {professionalsSearch.map((p) => (
                                <ListGroup.Item key={p.id ?? p.nameAndSurname} action onClick={() => handleProfessionalClick(p)} >
                                    <div className="opt">
                                        <div className="name">{p.nameAndSurname}</div>
                                        <div className="meta">
                                            {getServiceDescription(p.serviceIndicatorId)} &middot;
                                            Col. {p.registeredNumber} &middot;
                                            {getClaimProfessionalTypesDescription(p.typeProfessionalId)}
                                        </div>
                                    </div>
                                </ListGroup.Item>
                            ))}
                        </ListGroup>
                    )}
                </Row>
                <Row>
                    <Col xs={6} md={6}>
                        <Form.Label>{ t("claim:followUpComment.titleDemandTypeSelect") } </Form.Label>
                        <Form.Select
                            className = "form-control"
                            value = { formData.followupDemandType?.toString() ?? 0 }
                            onChange = {(e) => {
                                const selectedId = e.target.value === "" ? 0 : Number(e.target.value);
                                setFormData({...formData,followupDemandType: selectedId, });
                                const selectedType = claimFollowUpTypes?.find(t => t.id === selectedId);
                                if (selectedType?.isUpdateInformation && !claimUpdate.internalReportRequestDate) {
                                    setClaimUpdate({...claimUpdate,internalReportRequestDate: new Date().setHours(0, 0, 0, 0),});
                                }
                            }}>
                                <option value="" > </option>
                            {
                                claimFollowUpTypes?.map((claimFollowUpType) => (
                                    <option key= { claimFollowUpType.id } value = { claimFollowUpType.id } >
                                    { claimFollowUpType.name }
                                    </option>
                                ))
                            }
                            </Form.Select>
                    </Col>
                </Row>
                <Row>
                    <Form.Group>
                        <Form.Label>{t("claim:detail.sendTo")}</Form.Label>
                        <Form.Control
                            type="text"
                            value={formData.emails}
                            isInvalid={!!errors.emails}
                            onChange = {(e) => setFormData({
                                ...formData,
                                emails: e.target.value,
                            })}
                        />
                        <Form.Control.Feedback type="invalid">
                            {errors.emails}
                        </Form.Control.Feedback>
                    </Form.Group>
                </Row>
                <Row>
                    <Form.Group>
                        <Form.Label>{t("claim:detail.subject")}</Form.Label>
                        <Form.Control
                            type="text"
                            value={formData.subject}
                            isInvalid={!!errors.subject}
                            onChange = {(e) => setFormData({
                                ...formData,
                                subject: e.target.value,
                            })}
                        />
                        <Form.Control.Feedback type="invalid">
                            {errors.subject}
                        </Form.Control.Feedback>
                    </Form.Group>
                </Row>
                <Row>
                    <Form.Group>
                        <Form.Label>{t("claim:detail.emailMessage")}</Form.Label>
                        <Form.Control
                            as="textarea"               
                            rows={8}   
                            value={formData.message}
                            isInvalid={!!errors.message}
                            onChange = {(e) => setFormData({
                                ...formData,
                                message: e.target.value,
                            })}                        />
                        <Form.Control.Feedback type="invalid">
                            {errors.subject}
                        </Form.Control.Feedback>
                    </Form.Group>
                </Row>
                <Row>
                    <Col xs={12} md={12}>
                        <Form.Control
                            className="d-none"
                            type="file"
                            ref={fileInputRef}
                            onChange={handleFileChange}
                            multiple
                        />
                        <Row className="mt-3">
                            <Col md={10}>
                                {((formData.files || []).length > 0 || (formData.claimFiles || []).length > 0) &&
                                    <Stack direction="horizontal" gap={2} className="d-flex align-items-center gap-2 flex-wrap">
                                        <span><strong>{t("claim:followUpComment.fileListAttachment")}</strong></span>
                                        {(formData.files || []).map((file, index) => {
                                            return (
                                                <SelectedFilterChip
                                                    key={`${file.name}-${index}`}
                                                    label=""
                                                    value={file.name}
                                                    onRemove={() =>
                                                        handleRemoveFile(index)
                                                    }
                                                />
                                            );
                                        })}
                                        {(formData.claimFiles || []).map((file, index) => {
                                            return (
                                                <SelectedFilterChip
                                                    key={`${file.fileName}-${index}`}
                                                    label=""
                                                    value={file.fileName}
                                                    onRemove={() =>
                                                        handleRemoveClaimFile(index)
                                                    }
                                                />
                                            );
                                        })}
                                    </Stack>
                                }
                            </Col>
                            <Col md={2} className="d-flex justify-content-end align-items-start">
                                <a href="#" className="ms-auto" onClick={() => setShowAttachDocumentsModal(true)}>
                                    <p className="text-secondary mb-0">
                                        {t("claim:followUpReply.buttonAttachment")}
                                    </p>
                                </a>
                            </Col>
                        </Row>
                    </Col>
                 </Row>
                <Row className="mt-2">
                    <Col  className="text-end">
                        <Button variant="secondary" onClick={() => setSendEmail(false)}>
                            {t("common:buttons.buttonCancel")}
                        </Button>                        
                        <Button variant="primary" className="ms-2" onClick={() => handleSendEmail()}>
                            {t("common:buttons.buttonSend")}
                        </Button>                        
                    </Col>
                </Row>
            </Row>
        </div>
  );
}

